import { DatePipe } from "@angular/common";
import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { shortDateFormat } from "src/app/shared/dateTimeHelpers";
import { DataListTagGroup } from "src/app/shared/models/data-list";
import { KeyValuePair } from "src/app/shared/models/key-value-pair";
import { AdministrativeUnit } from "src/app/shared/models/organizations/administrative-unit";
import { withSanitizedName } from "src/app/shared/utilities/file-name-sanitizer";
import { DialogData } from "../upload-modal/upload-modal.models";

@Component({
    templateUrl: "./upload-modal-short.component.html",
    styleUrls: ["./upload-modal-short.component.scss"],
    standalone: false
})
export class UploadModalShortComponent implements OnInit {
  @ViewChild("fileInput") fileInput;
  @Output() uploadFiles = new EventEmitter();

  formArray: UntypedFormArray;
  dateFormat: string = shortDateFormat;
  pipe = new DatePipe("en-US");
  documentationTypeOptions: KeyValuePair[];
  cardHeader: string;
  fileTitle: string;
  maxFileSize: number;
  maxFileSizeLabel: string;
  availableTags: DataListTagGroup[] = [];
  preselectedTagIds: string[] = [];
  allowedFileTypes: string =
    "TXT, CSV, PDF, DOC, DOCX, ODT, PPTX, PPT, RTF, XLS, XLSX, XLSM, PNG, JPEG, JPG, GIF, BMP, ZIP";

  listOfFiles: File[] = [];
  errors: string[] = [];
  isPublicVisible: boolean;

  selectedTagsCount: number;

  selectedSchoolYear: string = "";
  selectedDocumentType: string = "";
  selectedAdministrativeUnit: string = "";
  selectedReleaseDate: Date;
  selectedAdministrativeUnitSurveyId: string = "";
  selectedStudentId: string = "";
  selectedIndicatorType: number = -1;
  selectedIsPublic: boolean = false;

  preselectedFiles: File[];

  get formArrayControls() {
    return this.formArray.controls as UntypedFormGroup[];
  }

  constructor(
    private dialogRef: MatDialogRef<UploadModalShortComponent>,
    private fb: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.fileTitle = data.fileName;
    this.cardHeader = data.title ?? "Upload Documentation";
    this.allowedFileTypes = data.allowedFileTypes ?? this.allowedFileTypes;
    this.maxFileSize = data.maxFileSize ?? 209715200;
    this.maxFileSizeLabel = data.maxFileSizeLabel ?? "200 MB";
    this.selectedSchoolYear = data.preselectedSchoolYear;
    this.selectedDocumentType = "2"; //always document for now -- data.preselectedDocumentType;
    this.selectedAdministrativeUnit = data.preselectedAdministrativeUnit;
    this.preselectedFiles = data.preselectedFiles;
    this.selectedAdministrativeUnitSurveyId = data.preselectedAdministrativeUnitSurveyId;
    this.selectedStudentId = data.preselectedStudentId;
    this.selectedIndicatorType = data.preselectedIndicatorType;
    this.selectedReleaseDate = new Date();
    this.preselectedTagIds = data.preselectedTagIds;
    this.isPublicVisible = data.isPublicVisible;
  }

  ngOnInit(): void {
    this.errors = [];
    this.formArray = this.fb.array([]);
    this.selectedTagsCount = this.data.preselectedTagIds.length;

    if (!this.data.isStateUser) {
      this.selectedReleaseDate = new Date();
    } else {
      const fakeAllAuAdministrativeUnit = {
        id: "00000000-0000-0000-0000-000000000000",
        name: "All AUs",
      } as AdministrativeUnit;
      this.data.availableAdministrativeUnits.unshift(fakeAllAuAdministrativeUnit);
    }

    if (this.preselectedFiles) {
      this.errors = [];
      this.listOfFiles = [];
      this.selectFiles(this.preselectedFiles);
    }
  }

  openNativeFileSelector() {
    this.errors = [];
    this.listOfFiles = [];

    if (this.fileInput) this.fileInput.nativeElement.click();
  }

  selectFiles(files) {
    for (let key in files) {
      if (!isNaN(parseInt(key))) {
        const file = files[key];
        if (this.isValidFile(file)) this.listOfFiles.push(withSanitizedName(file));
      }
    }
    if (this.errors.length === 0) this.addFilesToList();
  }

  onFileSelected() {
    const files: { [key: string]: File } = this.fileInput.nativeElement.files;
    return this.selectFiles(files);
  }

  private isValidFile(file: File): boolean {
    if (!(file && file.name)) {
      this.errors.push(`Invalid file or files`);
      return false;
    }

    const ext = file.name.split(".").slice(-1)[0];
    if (!ext) {
      this.errors.push(`Cannot determine file type`);
      return false;
    }

    if (!this.allowedFileTypes.toLowerCase().includes(ext.toLowerCase())) {
      this.errors.push(`File type '${ext}' is not supported`);
      return false;
    }

    if (file.size === 0) {
      this.errors.push(`File cannot be empty: ${file.name}`);
      return false;
    }

    if (file.size > this.maxFileSize) {
      this.errors.push(
        `File cannot be larger than ${this.maxFileSizeLabel}: ${file.name}`
      );
      return false;
    }

    return true;
  }

  onRemoveAt(index: number) {
    this.formArray.removeAt(index);
  }

  addFilesToList() {
    const formArray = this.formArray;
    formArray.clear();

    for (const file of this.listOfFiles) {
      formArray.push(
        this.fb.group({
          title: [this.fileTitle, Validators.required],
          type: [""],
          file: [file],
          isPublic: false,
        })
      );
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  confirm() {
    this.errors = [];

    if (this.formArray.invalid) {
      this.formArray.markAllAsTouched();
      return;
    }

    var results = {
      formArray: this.formArray.value,
      selectedTagIds: this.preselectedTagIds,
      selectedDocumentType: this.selectedDocumentType,
      selectedSchoolYear: this.selectedSchoolYear,
      selectedAdministrativeUnit: this.selectedAdministrativeUnit,
      selectedAdministrativeUnitSurveyId: this.selectedAdministrativeUnitSurveyId,
      selectedStudentId: this.selectedStudentId,
      selectedIndicatorType: this.selectedIndicatorType,
      selectedReleaseDate: this.selectedReleaseDate.toISOString(),
      selectedIsPublic: this.selectedIsPublic,
    };

    this.uploadFiles.emit(results);
  }

  hasRequiredItems() {
    return (
      this.selectedTagsCount > 0 &&
      this.selectedDocumentType &&
      this.selectedSchoolYear &&
      this.selectedAdministrativeUnit &&
      this.selectedReleaseDate &&
      this.listOfFiles.length > 0 &&
      this.formArray.valid
    );
  }
}
